import { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { logger } from '@/shared/lib/logger';

interface MarkdownProps {
  content: string;
  className?: string;
  /** When true, adds stable `id` anchors to h2/h3 headings for in-page TOC links. */
  headingAnchors?: boolean;
  /** Ordered heading ids (matching extractMarkdownHeadings) used to assign anchors. */
  headingIds?: string[];
}

// Configure marked options
marked.setOptions({
  gfm: true,
  breaks: true,
});

export interface MarkdownHeading {
  id: string;
  text: string;
  level: number;
}

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Extracts h2/h3 headings from markdown source (fence-aware) so a table of
 * contents can be rendered alongside the article. IDs are deduplicated and
 * are consumed in order by <Markdown headingAnchors headingIds=.../>.
 */
export function extractMarkdownHeadings(content: string): MarkdownHeading[] {
  const headings: MarkdownHeading[] = [];
  const seen = new Map<string, number>();
  const lines = content.split('\n');
  let inFence = false;

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (/^```/.test(trimmed)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const match = /^(#{2,3})\s+(.+)$/.exec(trimmed);
    if (!match) continue;
    const level = match[1].length;
    const text = match[2].trim().replace(/[*_`~]/g, '').replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').trim();

    let id = slugifyHeading(text);
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    if (count > 0) id = `${id}-${count + 1}`;

    headings.push({ id, text, level });
  }
  return headings;
}

/**
 * Adds `id` attributes to the rendered h2/h3 elements using the given heading
 * ids in document order. This runs AFTER marked has rendered (using its default
 * renderer), so we never have to reimplement inline/renderer methods by hand.
 */
function injectHeadingIds(html: string, headingIds: string[]): string {
  if (headingIds.length === 0) return html;

  let index = 0;
  return html.replace(/<h([23])([^>]*)>([\s\S]*?)<\/h\1>/g, (match, _level, attrs, inner) => {
    if (index >= headingIds.length) return match;
    const id = headingIds[index];
    index += 1;
    // Avoid overwriting an existing id, and skip headings already carrying one.
    if (/\bid=/.test(attrs)) return match;
    return `<h${_level}${attrs} id="${id}">${inner}</h${_level}>`;
  });
}

function parseMarkdown(content: string, headingAnchors: boolean, headingIds: string[]): string {
  if (!content) return '';
  try {
    let parsed = marked.parse(content, { async: false }) as string;
    if (headingAnchors) {
      parsed = injectHeadingIds(parsed, headingIds);
    }
    return DOMPurify.sanitize(parsed);
  } catch (err) {
    logger.error('Error parsing markdown:', err);
    return DOMPurify.sanitize(content);
  }
}

export function Markdown({
  content,
  className = '',
  headingAnchors = false,
  headingIds = [],
}: MarkdownProps) {
  const html = useMemo(
    () => parseMarkdown(content, headingAnchors, headingIds),
    [content, headingAnchors, headingIds]
  );

  if (!content) return null;

  return <div className={`markdown-content ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
}
